import mixpanel from 'mixpanel-browser'

// Single source of truth for Mixpanel. No other file in the repo is allowed to
// import mixpanel-browser directly — see no-direct-mixpanel.test.js.

let _initialized = false
let _warned = false

export function initAnalytics() {
  if (_initialized) return
  const token = import.meta.env.VITE_MIXPANEL_TOKEN
  if (!token) {
    if (!_warned) {
      console.warn('[analytics] VITE_MIXPANEL_TOKEN not set — analytics disabled')
      _warned = true
    }
    return
  }

  mixpanel.init(token, {
    // EU data residency — without this, /track and /engage go to the US default
    // and are silently dropped. The recorder uses api-js.* and auto-routes.
    api_host: 'https://api-eu.mixpanel.com',
    // Scoped autocapture: keep rage/dead clicks (free UX bug detectors), drop
    // everything that duplicates our manual events or pads volume with noise.
    autocapture: {
      click: false,
      input: false,
      pageview: false,
      scroll: false,
      submit: false,
      rage_click: true,
      dead_click: true,
    },
    record_sessions_percent: 25,
    // SDK default is maskAll=true (all replay text replaced with *). We opt out
    // so replays stay readable, then mask specific PII via the .mp-mask class.
    record_mask_all_text: false,
    record_mask_text_class: 'mp-mask',
    persistence: 'localStorage',
    // Page views are tracked manually via useTrackPageView so we control the
    // event name and properties.
    track_pageview: false,
  })
  _initialized = true
}

export function identifyUser({ email, name, picture } = {}) {
  if (!_initialized || !email) return
  mixpanel.identify(email)

  // Super-property (not a person property): attaches to every event for breakdown
  // analyses ("how does engagement change over a user's first 7 days?"). A person
  // property would only sit on the profile and couldn't slice events by tenure.
  try {
    const key = `aeo:first_seen:${email}`
    let firstSeen = localStorage.getItem(key)
    if (!firstSeen) {
      firstSeen = new Date().toISOString()
      localStorage.setItem(key, firstSeen)
    }
    const tenureDays = Math.floor((Date.now() - new Date(firstSeen).getTime()) / 86_400_000)
    mixpanel.register({ tenure_days: tenureDays })
  } catch {
    // localStorage unavailable (e.g. private mode quota) — skip tenure tracking
  }

  mixpanel.people.set({
    $email: email,
    $name: name,
    $avatar: picture,
  })
}

export function resetAnalytics() {
  if (!_initialized) return
  mixpanel.reset()
}

export function trackEvent(eventName, properties = {}) {
  if (!_initialized) return
  mixpanel.track(eventName, properties)
}

export function trackPageView(path, properties = {}) {
  trackEvent('Page Viewed', { path, ...properties })
}
